import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Layout from '@/components/Layout';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

const RICH_TAG_RE = /<(p|br|h2|h3|strong|b|em|i|u|ul|ol|li|a)\b/i;
const RTE_TAGS = ['p', 'br', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a'];
const linkHook = (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
};
const renderBody = (body) => {
  DOMPurify.addHook('afterSanitizeAttributes', linkHook);
  try {
    return DOMPurify.sanitize(body || '', { ALLOWED_TAGS: RTE_TAGS, ALLOWED_ATTR: ['href', 'title', 'target', 'rel'] });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
};

export default function PlatformArticle() {
  const { contentType, contentId } = useParams();
  const [user, setUser] = useState(null);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isNews = contentType === 'news';
  const backTo = isNews ? '/employer/news?tab=updates' : '/employer/resources?tab=guides';
  const backLabel = isNews ? 'Back to News' : 'Back to Resources';

  useEffect(() => {
    (async () => {
      try {
        const [u, a] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/platform/content/${contentId}`, { withCredentials: true }),
        ]);
        setUser(u.data); setArticle(a.data);
      } catch (e) { /* handled below */ } finally { setLoading(false); }
    })();
  }, [contentId]);

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(backTo)} data-testid="platform-article-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-5 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </button>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : !article ? (
          <div className="text-center py-20" data-testid="platform-article-missing">
            <p className="text-slate-700 font-medium">Article not found</p>
            <p className="text-sm text-slate-400 mt-1">It may have been unpublished or removed.</p>
          </div>
        ) : (
          <article data-testid="platform-article">
            {article.featured_image && (
              <div className="w-full h-64 sm:h-80 rounded-2xl overflow-hidden bg-slate-100 mb-6">
                <img src={article.featured_image} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <p className="text-xs font-semibold tracking-wide uppercase text-brand mb-2">{isNews ? 'Workforce Update' : 'Employer Guide'}</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">{article.title}</h1>
            {article.published_at && <p className="text-sm text-slate-400 mb-6">{fmtDate(article.published_at)}</p>}
            {article.excerpt && <p className="text-lg text-slate-600 leading-relaxed mb-6">{article.excerpt}</p>}
            <div className="rte-content max-w-none text-[15px] text-slate-700 leading-relaxed" data-testid="platform-article-body">
              {RICH_TAG_RE.test(article.body || '')
                ? <div dangerouslySetInnerHTML={{ __html: renderBody(article.body) }} />
                : (article.body || '').split('\n').map((para, i) => para.trim() ? <p key={i} className="mb-4 whitespace-pre-line">{para}</p> : null)}
            </div>
          </article>
        )}
      </div>
    </Layout>
  );
}
